import { Bar } from 'react-chartjs-2';
import ChartDeferred from 'chartjs-plugin-deferred';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from 'chart.js';
import { autoComma, formatMonth } from '@/utils/parser';
import { MonthlySales } from '@/types/statistics';
import { useEffect, useMemo, useState, useRef } from 'react';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ChartDeferred,
  ChartDataLabels,
);

interface BarChartProps {
  monthlySalesList: Array<MonthlySales>;
  onClickBar: (salesData: MonthlySales) => void;
  activedMonthlySales: MonthlySales;
  PcVersion?: boolean;
}

export const BarChart = ({
  monthlySalesList,
  onClickBar,
  activedMonthlySales,
  PcVersion = false,
}: BarChartProps) => {
  const labels = monthlySalesList?.map((item) => formatMonth(item.Month));
  const salesData = monthlySalesList?.map((item) => item.SalesAmount);
  const [maxSales, setMaxSales] = useState<number>(0);

  // 차트 ref 추가
  const chartRef = useRef<any>(null);

  const data: ChartData<'bar'> = {
    labels,
    datasets: [
      {
        label: '월 매출',
        data: salesData,
        backgroundColor: (context) => {
          const activedIndex = monthlySalesList.indexOf(activedMonthlySales);
          if (context.dataIndex === activedIndex) {
            return PcVersion ? '#9E95FF' : '#D9D5FF';
          } else {
            return '#EFEFEF';
          }
        },
        borderWidth: 0,
        barThickness: 30,
        borderRadius: {
          topLeft: 6,
          topRight: 6,
          bottomLeft: 2,
          bottomRight: 2,
        },
        minBarLength: 4,
      },
    ],
  };

  const options: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          align: 'top',
          anchor: 'end',
          font: {
            size: 12,
            weight: '500',
            lineHeight: '18px',
            family: 'Pretendard',
          },
          color: '#53555E',
          formatter: (amount: number) => {
            if (amount >= 10000) {
              const unit = Math.floor(amount / 10000);
              return `${autoComma(unit)}만원`;
            } else if (amount > 0) {
              const unit = Math.floor(amount / 1000);
              return `${autoComma(unit)}천원`;
            } else {
              return '0원';
            }
          },
          offset: 0,
        },
      },
      scales: {
        x: {
          type: 'category',
          border: {
            width: 1,
            color: '#EFEFEF',
          },
          grid: {
            display: false,
          },
          ticks: {
            color: '#53555E',
          },
        },
        y: {
          display: false,
          suggestedMax: maxSales * 1.2,
        },
      },
      onClick: (_, elements) => {
        if (!elements.length) return;

        const element = elements[0];
        const index = element.index;
        const clickedData = monthlySalesList[index];

        onClickBar(clickedData);
      },
      animation: {
        duration: 0,
      },
      elements: {
        bar: {
          // pressed 시, 어두워지는 기존 스타일 제거
          hoverBackgroundColor: (context) => {
            const activedIndex = monthlySalesList.indexOf(activedMonthlySales);
            if (context.dataIndex === activedIndex) {
              return PcVersion ? '#9E95FF' : '#D9D5FF';
            } else {
              // return '#EFEFEF';
              return PcVersion ? '#9E95FF' : '#F2F0FF';
            }
          },
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: false,
        axis: 'x',
      },
    }),
    [monthlySalesList, maxSales, activedMonthlySales, PcVersion, onClickBar],
  );

  useEffect(() => {
    if (monthlySalesList.length === 0) return;

    const maxAmount = Math.max(...monthlySalesList.map((item) => item.SalesAmount));
    setMaxSales(maxAmount);
  }, [monthlySalesList]);

  // 커서 pointer 처리
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // react-chartjs-2 v4에서는 chartRef.current.canvas로 접근
    const canvas = chart.canvas;
    if (!canvas) return;

    const handleMouseMove = (event: MouseEvent) => {
      const chartInstance = chart;
      if (!chartInstance) return;

      // getElementsAtEventForMode는 react-chartjs-2 v4 이상에서 사용
      const elements = chartInstance.getElementsAtEventForMode(
        event,
        'nearest',
        { intersect: true },
        false,
      );
      if (elements.length) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'default';
      }
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [monthlySalesList, activedMonthlySales, PcVersion]);

  return (
    <div className="h-[156px] w-full ">
      <Bar ref={chartRef} data={data} options={options} />
    </div>
  );
};
